// Release B-Fidelity-2b · Slice F frontend — Owner availability admin.
// Route: /admin/owner/schedule
import React, { useEffect, useState } from "react";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import InformationIntake, { AttachmentsPanel } from "../../design-system/intake/InformationIntake";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const ALL_METHODS = ["Phone", "Teams", "Video", "ERF_Office", "Project_Site", "Other"];

const empty = {
  day_of_week: 1, start_time_local: "09:00", end_time_local: "12:00",
  timezone: "America/New_York", duration_minutes: 30, prep_buffer_minutes: 10,
  travel_buffer_minutes: 0, methods: ["Phone"], auto_confirm_methods: [],
  blocked_dates: [], active: true, display_name: "Edwin's Team",
};

export default function OwnerSchedule() {
  const [rows, setRows] = useState([]);
  const [providerState, setProviderState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [attachReload, setAttachReload] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const [av, ps] = await Promise.all([
        api.get("/admin/owner/schedule/availability"),
        api.get("/admin/owner/schedule/provider-state"),
      ]);
      setRows(av.data.items || []);
      setProviderState(ps.data);
    } catch (e) { toast.error("Failed to load availability"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleMethod = (m, field) => setForm((f) => {
    const cur = new Set(f[field] || []);
    cur.has(m) ? cur.delete(m) : cur.add(m);
    return { ...f, [field]: [...cur] };
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/admin/owner/schedule/availability",
                     { ...form, id: editingId || undefined });
      toast.success(editingId ? "Availability updated" : "Availability created");
      setForm(empty); setEditingId(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Save failed");
    } finally { setSaving(false); }
  };
  const edit = (r) => { setEditingId(r.id); setForm({ ...empty, ...r }); };
  const remove = async (id) => {
    if (!window.confirm("Deactivate this availability window?")) return;
    try { await api.delete(`/admin/owner/schedule/availability/${id}`); toast.success("Deactivated"); await load(); }
    catch { toast.error("Failed to deactivate"); }
  };

  return (
    <AdminAppShell current="/admin/owner/schedule">
      <div data-testid="owner-schedule-page" style={{ padding: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>Interview Scheduling</h1>
            <div style={{ color: "#475569", fontSize: 13, marginTop: 4 }}>
              Define the windows Edwin's team can offer applicants after portal-access review.
            </div>
          </div>
          {providerState && (
            <div data-testid="schedule-provider-state" style={{ fontSize: 12, color: "#475569" }}>
              Provider: <b>{providerState.provider}</b> ·
              <span style={{ marginLeft: 6, color: providerState.active ? "#22c55e" : "#dc2626" }}>
                {providerState.active ? "active" : "inactive"}
              </span>
            </div>
          )}
        </div>

        <Card style={{ marginTop: 16 }}>
          <CardContent style={{ padding: 20 }}>
            <h2 style={{ marginTop: 0 }}>{editingId ? "Edit window" : "Add window"}</h2>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
              <Field label="Day of week">
                <select data-testid="sch-day" value={form.day_of_week} onChange={(e) => setForm({ ...form, day_of_week: Number(e.target.value) })} style={inputStyle}>
                  {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                </select>
              </Field>
              <Field label="Start (HH:MM)">
                <Input data-testid="sch-start" value={form.start_time_local} onChange={(e) => setForm({ ...form, start_time_local: e.target.value })} />
              </Field>
              <Field label="End (HH:MM)">
                <Input data-testid="sch-end" value={form.end_time_local} onChange={(e) => setForm({ ...form, end_time_local: e.target.value })} />
              </Field>
              <Field label="Slot duration (min)">
                <Input data-testid="sch-duration" type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: Number(e.target.value) })} />
              </Field>
              <Field label="Prep buffer (min)">
                <Input data-testid="sch-prep" type="number" value={form.prep_buffer_minutes} onChange={(e) => setForm({ ...form, prep_buffer_minutes: Number(e.target.value) })} />
              </Field>
              <Field label="Travel buffer (min)">
                <Input data-testid="sch-travel" type="number" value={form.travel_buffer_minutes} onChange={(e) => setForm({ ...form, travel_buffer_minutes: Number(e.target.value) })} />
              </Field>
              <Field label="Valid from (YYYY-MM-DD)">
                <Input data-testid="sch-valid-from" value={form.valid_from || ""} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} />
              </Field>
              <Field label="Valid until (YYYY-MM-DD)">
                <Input data-testid="sch-valid-until" value={form.valid_until || ""} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
              </Field>
              <Field label="Display name to applicant">
                <Input data-testid="sch-display" value={form.display_name || ""} onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
              </Field>
            </div>
            <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              <div>
                <div style={{ fontSize: 11, textTransform: "uppercase", color: "#475569", letterSpacing: 1 }}>Methods offered</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
                  {ALL_METHODS.map((m) => (
                    <label key={m} data-testid={`sch-method-${m}`} style={pillLabel(form.methods.includes(m))}>
                      <input type="checkbox" checked={form.methods.includes(m)} onChange={() => toggleMethod(m, "methods")} style={{ display: "none" }} />
                      {m}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, textTransform: "uppercase", color: "#475569", letterSpacing: 1 }}>Auto-confirm methods</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
                  {ALL_METHODS.filter((m) => form.methods.includes(m)).map((m) => (
                    <label key={m} data-testid={`sch-auto-${m}`} style={pillLabel(form.auto_confirm_methods.includes(m))}>
                      <input type="checkbox" checked={form.auto_confirm_methods.includes(m)} onChange={() => toggleMethod(m, "auto_confirm_methods")} style={{ display: "none" }} />
                      {m}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <Field label="Blocked dates (comma-sep YYYY-MM-DD)" style={{ marginTop: 16 }}>
              <Input data-testid="sch-blocked" value={(form.blocked_dates || []).join(", ")}
                     onChange={(e) => setForm({ ...form, blocked_dates: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
            </Field>
            <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
              <Button data-testid="sch-save" onClick={save} disabled={saving}>{editingId ? "Save changes" : "Add window"}</Button>
              {editingId && <Button data-testid="sch-cancel" variant="secondary" onClick={() => { setForm(empty); setEditingId(null); }}>Cancel</Button>}
            </div>
          </CardContent>
        </Card>

        <Card style={{ marginTop: 20 }}>
          <CardContent style={{ padding: 0 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f8fafc", textAlign: "left" }}>
                  <th style={hStyle}>Day</th><th style={hStyle}>Time</th>
                  <th style={hStyle}>Duration</th><th style={hStyle}>Methods</th>
                  <th style={hStyle}>Buffers</th><th style={hStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={6} style={{ padding: 16, color: "#64748b" }}>Loading…</td></tr>}
                {!loading && rows.length === 0 && <tr><td colSpan={6} style={{ padding: 16, color: "#64748b" }}>No availability windows yet — add one above.</td></tr>}
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`sch-row-${r.id}`} style={{ borderTop: "1px solid #e5e7eb" }}>
                    <td style={cStyle}>{DAYS[r.day_of_week]}</td>
                    <td style={cStyle}>{r.start_time_local}–{r.end_time_local}</td>
                    <td style={cStyle}>{r.duration_minutes}m</td>
                    <td style={cStyle}>{(r.methods || []).join(", ")}<div style={{ fontSize: 10, color: "#64748b" }}>auto: {(r.auto_confirm_methods || []).join(", ") || "—"}</div></td>
                    <td style={cStyle}>{r.prep_buffer_minutes}+{r.travel_buffer_minutes}m</td>
                    <td style={cStyle}>
                      <Button data-testid={`sch-edit-${r.id}`} size="sm" variant="secondary" onClick={() => edit(r)}>Edit</Button>
                      <Button data-testid={`sch-del-${r.id}`} size="sm" variant="destructive" onClick={() => remove(r.id)} style={{ marginLeft: 8 }}>Deactivate</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
        <Card style={{ marginTop: 20 }}>
          <CardContent style={{ padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "#64748b" }}>Bulk import (3c)</div>
                <div style={{ fontSize: 13, marginTop: 2 }}>
                  Upload a CSV of Owner availability windows or paste blocked dates. Rows land as unverified preview attachments
                  — nothing writes to `owner_availability` until you Confirm each row.
                </div>
              </div>
              <InformationIntake
                recordType="owner_availability" recordId="module-schedule"
                category="import_csv" confidentiality="internal"
                capabilities={["import_csv", "paste", "manual"]}
                onChanged={() => setAttachReload((k) => k + 1)}
              />
            </div>
            <AttachmentsPanel recordType="owner_availability" recordId="module-schedule" reload={attachReload} />
          </CardContent>
        </Card>
      </div>
    </AdminAppShell>
  );
}

const inputStyle = { width: "100%", padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: 4, fontSize: 13, background: "white", boxSizing: "border-box" };
const hStyle = { padding: 10, fontSize: 11, textTransform: "uppercase", color: "#64748b", letterSpacing: 1 };
const cStyle = { padding: 10, fontSize: 13, verticalAlign: "top" };
function Field({ label, children, style }) {
  return <label style={{ display: "grid", gap: 4, fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "#475569", ...style }}><span>{label}</span>{children}</label>;
}
const pillLabel = (on) => ({
  padding: "6px 12px", borderRadius: 9999, cursor: "pointer", userSelect: "none",
  fontSize: 12, fontWeight: 600,
  background: on ? "#12233F" : "white", color: on ? "#F5F1E8" : "#12233F",
  border: `1px solid ${on ? "#12233F" : "#cbd5e1"}`,
});
